/* eslint-disable react-hooks/rules-of-hooks */
import { useLazyQuery, useQuery, NetworkStatus } from "@apollo/client";
import moment from "moment-timezone";
import { useMemo } from "react";

import {
  GET_OWN_OEM_TICKETS_BY_STATUS,
  GET_OWN_OEM_TICKETS_BY_TYPES_QUERY,
  GET_OWN_OEM_TICKET_BY_ID,
  GET_SINGLE_LIST_TICKET_BY_ID_QUERY,
  LIST_OEM_CUSTOMERS_IN_AREA,
  LIST_OWN_OEM_ALL_TICKETS_QUERY,
  LIST_OWN_OEM_CUSTOM_ADDITIONAL_FIELDS_QUERY,
  LIST_OWN_OEM_KANBAN_TICKETS,
  LIST_OWN_OEM_OPEN_TICKETS_QUERY,
  LIST_OWN_OEM_SUPPORT_ACCOUNTS_QUERY,
  LIST_OWN_OEM_SCHEDULER_AGENTS_QUERY,
  LIST_OWN_OEM_TICKETS_QUERY,
  LIST_OWN_OEM_ARCHIVED_TICKETS_QUERY,
  LIST_OWN_OEM_USER_CLOSED_TICKETS_QUERY,
  LIST_OWN_OEM_USER_OPEN_TICKETS_QUERY,
  LIST_OWN_OEM_ASSIGNEES_QUERY,
  LIST_OWN_TICKET_MENTIONABLE_USERS_QUERY,
  DOWNLOAD_WO_PDF_BY_ID,
  LIST_ALL_OEM_CUSTOMERS_IN_AREA,
  LIST_OWN_OEM_ASSET_HIERARCHY_TICKETS,
  LIST_ASSIGNABLE_USERS_QUERY,
  LIST_GROUPED_TICKETS_QUERY,
  GET_OWN_OEM_CUSTOMER_MAP_DATA_BY_ID,
  LIST_CUSTOMERS_WITH_TICKETS,
  LIST_OWN_OEM_UNSCHEDULED_TICKETS_QUERY,
  LIST_OWN_OEM_SCHEDULED_TICKETS_QUERY,
  GET_OWN_OEM_SCHEDULER_TICKET_BY_ID,
} from "~/api";
import { queryTracker } from "~/atoms";
import { ITEMS_PER_PAGE } from "~/constants";
import { ticketTransformer } from "~/transformers";
import { generateFacilitiesQueryWhereCond } from "~/utils/filter/_facilitiesQueryCond";
import {
  generateListOwnOemTicketsVariables,
  generateOpenTicketQueryWhereCond,
} from "~/utils/filter/_openTicketQueryCond";

/**
 * @deprecated Use `useGetWorkOrderById` from `~/services/workOrders` for new
 * code. Kept here while the legacy SingleTicket page is still in use.
 */
export const getTicketById = (id, skipCondition = false) => {
  const {
    data: { getOwnOemTicketById } = {},
    loading,
    refetch: refetchGetOwnOemTicketById,
  } = useQuery(GET_OWN_OEM_TICKET_BY_ID, {
    variables: {
      id,
    },
    skip: !id || skipCondition,
    fetchPolicy: "network-only",
  });

  return {
    groupedByDate: ticketTransformer.groupedByDate(getOwnOemTicketById),
    ticket: getOwnOemTicketById,
    loading,
    refetch: refetchGetOwnOemTicketById,
  };
};

export const getScheduledTicketById = (id, skipCondition = false) => {
  const {
    data: { getOwnOemTicketById } = {},
    loading,
    refetch: refetchGetOwnOemTicketById,
  } = useQuery(GET_OWN_OEM_SCHEDULER_TICKET_BY_ID, {
    variables: {
      id,
    },
    skip: !id || skipCondition,
  });

  return {
    ticket: getOwnOemTicketById,
    loading,
    refetch: refetchGetOwnOemTicketById,
  };
};

export const listCustomAdditionalFields = () => {
  const {
    data: { listOwnOemCustomFields: customAdditionalFields = [] } = {},
    loading,
  } = useQuery(LIST_OWN_OEM_CUSTOM_ADDITIONAL_FIELDS_QUERY, {
    variables: {
      type: "tickets",
    },
    fetchPolicy: "network-only",
  });

  return {
    customAdditionalFields,
    loading,
  };
};

export const listAssignees = ({ skipCondition = false }) => {
  const {
    data: { listOwnOemSupportAccounts = [] } = {},
    loading,
    error,
  } = useQuery(LIST_OWN_OEM_ASSIGNEES_QUERY, { skip: skipCondition });

  return {
    error,
    loading,
    listOwnOemSupportAccounts,
  };
};

export const listSupportAccounts = (variables) => {
  const {
    data: { listOwnOemSupportAccounts = [] } = {},
    loading,
    error,
  } = useQuery(LIST_OWN_OEM_SUPPORT_ACCOUNTS_QUERY, {
    variables,
  });

  return {
    error,
    loading,
    listOwnOemSupportAccounts,
    supportAccountOptions: ticketTransformer.makeOptions(
      listOwnOemSupportAccounts,
    ),
  };
};

export const listSchedulerAgents = () => {
  const {
    data: { listOwnOemSchedulerAgents = [] } = {},
    loading,
    error,
    networkStatus,
  } = useQuery(LIST_OWN_OEM_SCHEDULER_AGENTS_QUERY, {
    // Never paint scheduler hatching from a stale cache after hours edits.
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
  });

  const agents = useMemo(
    () =>
      ticketTransformer.makeOptions(
        listOwnOemSchedulerAgents.filter((agent) => !agent.deleted),
      ),
    [listOwnOemSchedulerAgents],
  );

  return {
    error,
    loading: loading || networkStatus === NetworkStatus.loading,
    agents,
  };
};

export const useListAssignableUsers = (variables, skipCondition = false) => {
  const {
    data: { listAssignableUsers = [] } = {},
    loading,
    error,
  } = useQuery(LIST_ASSIGNABLE_USERS_QUERY, {
    variables,
    skip: skipCondition,
    // Gates which members the assignee dropdown lets you pick. It moves with
    // the work order's teams (asset/company writes refetch it) and with team
    // membership changed elsewhere; cache-first would keep serving the stale
    // set, rendering newly-assignable members disabled until a page reload.
    fetchPolicy: "cache-and-network",
  });

  return {
    error,
    loading,
    listAssignableUsers,
  };
};

export const listTicketMentionableUsers = (
  variables,
  skipCondition = false,
) => {
  const {
    data: { listOwnTicketMentionableUsers } = {
      facilityUsers: [],
      oemUsers: [],
    },
    loading,
    error,
  } = useQuery(LIST_OWN_TICKET_MENTIONABLE_USERS_QUERY, {
    variables,
    skip: skipCondition,
  });

  return {
    error,
    loading,
    listOwnTicketMentionableUsers,
  };
};

// LIST_OEM_CUSTOMERS_IN_AREA

export const useListOemCustomersInArea = ({ where = {}, activeFilters }) => {
  const variables = generateListOwnOemTicketsVariables({
    activeFilters,
    where,
  });

  const {
    data: { listOemCustomersInArea: data = [] } = {},
    loading,
    error,
    refetch,
  } = useQuery(LIST_OEM_CUSTOMERS_IN_AREA, {
    variables,
    fetchPolicy: "network-only",
  });
  return {
    loading,
    error,
    data,
    refetch,
  };
};

export const listOwnOemAllTickets = (activeFilters) => {
  const filterConditions = generateOpenTicketQueryWhereCond(activeFilters);
  const hasFilter = Object.keys(filterConditions)?.length;
  const variables = {
    ...(hasFilter
      ? {
          params: {
            where: filterConditions,
          },
        }
      : {}),
  };
  queryTracker.add([{ key: "LIST_OWN_OEM_ALL_TICKETS_QUERY", variables }]);

  const {
    data: { listOwnOemAllTickets: data = [] } = {},
    loading,
    error,
    refetch,
  } = useQuery(LIST_OWN_OEM_ALL_TICKETS_QUERY, {
    variables,
  });
  return {
    loading,
    error,
    data,
    refetch,
  };
};

export const useGetOwnOemConnectionMapDataById = (id) => {
  const {
    data: { getOwnOemCustomerById: connection = {} } = {},
    error,
    loading,
    refetch,
  } = useQuery(GET_OWN_OEM_CUSTOMER_MAP_DATA_BY_ID, {
    variables: {
      id,
    },
    fetchPolicy: "network-only",
    skip: !id,
  });
  return {
    connection,
    loading,
    error,
    refetch,
  };
};

// LIST_ALL_OEM_CUSTOMERS_IN_AREA

export const useListAllOemCustomersInArea = ({
  where = {},
  activeFilters,
  skip: skipCondition,
}) => {
  // const variables = generateListOwnOemTicketsVariables({
  //   activeFilters,
  //   where,
  // });

  const filterConditions = generateFacilitiesQueryWhereCond(activeFilters);
  const hasFilter = Object.keys(filterConditions)?.length;
  const whereCondition = hasFilter
    ? { ...filterConditions, ...(where || {}) }
    : { ...(where || {}) };

  const variables = {
    params: {
      where: whereCondition,
    },
  };

  const {
    data: { listAllOemCustomersInArea: data = [] } = {},
    loading,
    error,
    refetch,
  } = useQuery(LIST_ALL_OEM_CUSTOMERS_IN_AREA, {
    variables,
    fetchPolicy: "cache-and-network",
    skip: skipCondition,
  });
  return {
    loading,
    error,
    data,
    refetch,
  };
};

export const listOwnUserOemOpenTickets = () => {
  const variables = {};
  queryTracker.add([
    { key: "LIST_OWN_OEM_USER_OPEN_TICKETS_QUERY", variables },
  ]);

  const { data: { listOwnOemUserOpenTickets: data = [] } = {}, loading } =
    useQuery(LIST_OWN_OEM_USER_OPEN_TICKETS_QUERY, {
      variables,
    });

  return {
    loading,
    data,
  };
};

export const listOwnUserOemClosedTickets = () => {
  const variables = {};
  queryTracker.add([
    { key: "LIST_OWN_OEM_USER_CLOSED_TICKETS_QUERY", variables },
  ]);
  const { data: { listOwnOemUserClosedTickets: data = [] } = {}, loading } =
    useQuery(LIST_OWN_OEM_USER_CLOSED_TICKETS_QUERY, { variables });

  return {
    loading,
    data,
  };
};

export const useListOwnOemOpenTickets = ({ activeFilters }) => {
  const filterConditions = generateOpenTicketQueryWhereCond(activeFilters);
  const hasFilter = Object.keys(filterConditions)?.length;
  const variables = {
    ...(hasFilter
      ? {
          params: {
            where: filterConditions,
          },
        }
      : {}),
  };
  const {
    data: { listOwnOemOpenTickets = [] } = {},
    loading,
    error,
  } = useQuery(LIST_OWN_OEM_OPEN_TICKETS_QUERY, { variables });
  return {
    loading,
    error,
    listOwnOemOpenTickets,
  };
};

export const useCheckTicketHasStatus = () => {
  const [getTickets, { loading, error }] = useLazyQuery(
    GET_OWN_OEM_TICKETS_BY_STATUS,
  );

  return {
    loading,
    error,
    checkIfStatusIsUsed: async (statusId) => {
      const { data: { checkTicketHasStatus: isUsed } = {} } = await getTickets({
        variables: {
          statusId,
        },
        fetchPolicy: "network-only",
      });

      return isUsed;
    },
  };
};

export const useListOwnOemTickets = ({
  activeFilters,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery,
  where,
  skipCondition,
  sort = [],
  notifyOnNetworkStatusChange = true,
} = {}) => {
  const variables = generateListOwnOemTicketsVariables({
    activeFilters,
    limit,
    skip,
    searchQuery,
    where,
    sort,
  });
  queryTracker.add([{ key: "LIST_OWN_OEM_TICKETS_QUERY", variables }]);

  const { data, previousData, loading, refetch, fetchMore } = useQuery(
    LIST_OWN_OEM_TICKETS_QUERY,
    {
      variables,
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange,
      skip: skipCondition,
    },
  );

  // Keep prior rows while sort/filter variables change — avoids full-table
  // skeleton flash (new variables miss cache until the network responds).
  const listOwnOemTickets =
    data?.listOwnOemTickets ?? previousData?.listOwnOemTickets ?? {};

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    tickets = [],
  } = listOwnOemTickets;

  const handleFetchMore = ({ limit, skip }) => {
    const variables = generateListOwnOemTicketsVariables({
      activeFilters,
      limit,
      skip,
      searchQuery,
      where,
      sort,
    });
    fetchMore({
      variables,
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedTickets = [
          ...previousResult.listOwnOemTickets.tickets,
          ...fetchMoreResult.listOwnOemTickets.tickets,
        ];

        const updates = {
          ...previousResult,
          listOwnOemTickets: {
            ...previousResult.listOwnOemTickets,
            ...fetchMoreResult.listOwnOemTickets,
            tickets: mergedTickets,
          },
        };

        return updates;
      },
    });
  };

  return {
    tickets,
    handleFetchMore,
    refetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    loading,
  };
};

export const useListOwnOemArchivedTickets = ({
  activeFilters,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery,
  sort = [],
  skipCondition,
  notifyOnNetworkStatusChange = true,
  isHierarchicalView = false,
} = {}) => {
  const sortValue = sort.length ? sort : ["lastUpdatedAt:desc"];
  const where = { isHierarchicalView };
  const variables = generateListOwnOemTicketsVariables({
    activeFilters,
    limit,
    skip,
    searchQuery,
    where,
    sort: sortValue,
  });
  queryTracker.add([{ key: "LIST_OWN_OEM_ARCHIVED_TICKETS_QUERY", variables }]);

  const {
    data: { listOwnOemArchivedTickets = {} } = {},
    loading,
    refetch,
    fetchMore,
  } = useQuery(LIST_OWN_OEM_ARCHIVED_TICKETS_QUERY, {
    variables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange,
    skip: skipCondition,
  });

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    tickets = [],
  } = listOwnOemArchivedTickets;

  const handleFetchMore = ({ limit: fetchLimit, skip: fetchSkip }) => {
    const fetchVariables = generateListOwnOemTicketsVariables({
      activeFilters,
      limit: fetchLimit,
      skip: fetchSkip,
      searchQuery,
      where,
      sort: sortValue,
    });
    fetchMore({
      variables: fetchVariables,
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;
        const mergedTickets = [
          ...previousResult.listOwnOemArchivedTickets.tickets,
          ...fetchMoreResult.listOwnOemArchivedTickets.tickets,
        ];
        return {
          ...previousResult,
          listOwnOemArchivedTickets: {
            ...previousResult.listOwnOemArchivedTickets,
            ...fetchMoreResult.listOwnOemArchivedTickets,
            tickets: mergedTickets,
          },
        };
      },
    });
  };

  return {
    tickets,
    handleFetchMore,
    refetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    loading,
  };
};

export const useListOwnOemScheduledTickets = ({
  activeFilters,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery,
  where,
  skipCondition,
  sort = [],
  notifyOnNetworkStatusChange = true,
} = {}) => {
  const variables = generateListOwnOemTicketsVariables({
    activeFilters,
    limit,
    skip,
    searchQuery,
    where,
    sort,
  });
  queryTracker.add([
    { key: "LIST_OWN_OEM_SCHEDULED_TICKETS_QUERY", variables },
  ]);

  const {
    data: { listOwnOemTickets = {} } = {},
    loading,
    refetch,
    fetchMore,
  } = useQuery(LIST_OWN_OEM_SCHEDULED_TICKETS_QUERY, {
    variables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange,
    skip: skipCondition,
  });

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    tickets = [],
  } = listOwnOemTickets;

  const handleFetchMore = ({ limit, skip }) => {
    const variables = generateListOwnOemTicketsVariables({
      activeFilters,
      limit,
      skip,
      searchQuery,
      where,
      sort,
    });
    fetchMore({
      variables,
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedTickets = [
          ...previousResult.listOwnOemTickets.tickets,
          ...fetchMoreResult.listOwnOemTickets.tickets,
        ];

        const updates = {
          ...previousResult,
          listOwnOemTickets: {
            ...previousResult.listOwnOemTickets,
            ...fetchMoreResult.listOwnOemTickets,
            tickets: mergedTickets,
          },
        };

        return updates;
      },
    });
  };

  return {
    tickets,
    handleFetchMore,
    refetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    loading,
  };
};

export const useListOwnOemUnscheduledTickets = ({
  activeFilters,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery,
  where,
  skipCondition = false,
  sort = [],
  notifyOnNetworkStatusChange = true,
} = {}) => {
  const variables = generateListOwnOemTicketsVariables({
    activeFilters,
    limit,
    skip,
    searchQuery,
    where,
    sort,
  });
  queryTracker.add([
    { key: "LIST_OWN_OEM_UNSCHEDULED_TICKETS_QUERY", variables },
  ]);

  const {
    data: { listOwnOemTickets = {} } = {},
    loading,
    refetch,
    fetchMore,
  } = useQuery(LIST_OWN_OEM_UNSCHEDULED_TICKETS_QUERY, {
    variables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange,
    skip: skipCondition,
  });

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    tickets = [],
  } = listOwnOemTickets;

  const handleFetchMore = ({ limit, skip }) => {
    const variables = generateListOwnOemTicketsVariables({
      activeFilters,
      limit,
      skip,
      searchQuery,
      where,
      sort,
    });
    fetchMore({
      variables,
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedTickets = [
          ...previousResult.listOwnOemTickets.tickets,
          ...fetchMoreResult.listOwnOemTickets.tickets,
        ];

        const updates = {
          ...previousResult,
          listOwnOemTickets: {
            ...previousResult.listOwnOemTickets,
            ...fetchMoreResult.listOwnOemTickets,
            tickets: mergedTickets,
          },
        };

        return updates;
      },
    });
  };

  return {
    tickets,
    handleFetchMore,
    refetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    loading,
  };
};

export const useListGroupedTickets = ({
  activeFilters,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery,
  where,
  skipCondition,
  sort = [],
  notifyOnNetworkStatusChange = true,
} = {}) => {
  const variables = generateListOwnOemTicketsVariables({
    activeFilters,
    limit,
    skip,
    searchQuery,
    where,
    sort,
  });

  const { data: { listGroupedByCustomersTickets = [] } = {}, loading } =
    useQuery(LIST_GROUPED_TICKETS_QUERY, {
      variables,
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange,
      skip: skipCondition,
    });

  const groupedTickets = listGroupedByCustomersTickets;

  return {
    tickets: groupedTickets,
    loading,
  };
};

export const useListAllActiveCustomers = ({
  activeFilters,
  searchQuery,
  where,
  skipCondition,
  sort = [],
  notifyOnNetworkStatusChange = true,
} = {}) => {
  const variables = generateListOwnOemTicketsVariables({
    activeFilters,
    searchQuery,
    where,
    sort,
  });

  const { data: { listCustomersWithTickets = [] } = {}, loading } = useQuery(
    LIST_CUSTOMERS_WITH_TICKETS,
    {
      variables,
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange,
      skip: skipCondition,
    },
  );

  const activeCustomers = listCustomersWithTickets;

  return {
    activeCustomers,
    loading,
  };
};

export const useAssetHierarchyTickets = ({
  activeFilters,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery,
  where,
  skipCondition,
  sort = [],
  notifyOnNetworkStatusChange = true,
} = {}) => {
  const variables = generateListOwnOemTicketsVariables({
    activeFilters,
    limit,
    skip,
    searchQuery,
    where,
    sort,
  });

  const {
    data: { listOwnAssetHierarchyTickets = {} } = {},
    loading,
    refetch,
    fetchMore,
  } = useQuery(LIST_OWN_OEM_ASSET_HIERARCHY_TICKETS, {
    variables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange,
    skip: skipCondition,
  });

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    tickets = [],
  } = listOwnAssetHierarchyTickets;

  const handleFetchMore = ({ limit, skip }) => {
    const variables = generateListOwnOemTicketsVariables({
      activeFilters,
      limit,
      skip,
      searchQuery,
      where,
      sort,
    });
    fetchMore({
      variables,
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedTickets = [
          ...previousResult.listOwnAssetHierarchyTickets.tickets,
          ...fetchMoreResult.listOwnAssetHierarchyTickets.tickets,
        ];

        const updates = {
          ...previousResult,
          listOwnAssetHierarchyTickets: {
            ...previousResult.listOwnAssetHierarchyTickets,
            ...fetchMoreResult.listOwnAssetHierarchyTickets,
            tickets: mergedTickets,
          },
        };

        return updates;
      },
    });
  };

  return {
    tickets,
    handleFetchMore,
    refetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    loading,
  };
};

export const useListOwnOemKanbanTickets = ({
  activeFilters,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery,
  sort,
  where,
  skipCondition = false,
} = {}) => {
  const filterConditions = generateOpenTicketQueryWhereCond(activeFilters);
  const hasFilter = Object.keys(filterConditions)?.length;
  const variables = {
    ...(hasFilter
      ? {
          params: {
            where: {
              ...filterConditions,
              searchQuery,
              ...(where || {}),
            },
            limit,
            skip,
            ...(sort ? { sort } : {}),
          },
        }
      : {
          params: {
            limit,
            skip,
            ...(sort ? { sort } : {}),
            where: {
              searchQuery,
              ...(where || {}),
            },
          },
        }),
  };

  const {
    data: { listOwnOemKanbanTickets = {} } = {},
    loading,
    refetch: ticketsRefetch,
    fetchMore,
  } = useQuery(LIST_OWN_OEM_KANBAN_TICKETS, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
    skip: skipCondition,
  });

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    columns = [],
  } = listOwnOemKanbanTickets;

  return {
    columns,
    fetchMore,
    ticketsRefetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    loading,
  };
};

export const useCheckTicketHasWorkOrderType = () => {
  const [checkTickets, { loading, error }] = useLazyQuery(
    GET_OWN_OEM_TICKETS_BY_TYPES_QUERY,
  );
  return {
    loading,
    error,
    checkIfWorkOrderTypeUsed: async (ticketTypeId) => {
      const { data: { checkIfTicketsExistForType } = {} } = await checkTickets({
        variables: { ticketTypeId },
      });
      return !!checkIfTicketsExistForType;
    },
  };
};

export const useGetTicketByIdForList = () => {
  const [getTicket] = useLazyQuery(GET_SINGLE_LIST_TICKET_BY_ID_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  return { getTicketById: async (id) => getTicket({ variables: { id } }) };
};

export const useGetWOPdfById = () => {
  const [download, { data: { downloadWOPDF = "" } = {}, loading, error }] =
    useLazyQuery(DOWNLOAD_WO_PDF_BY_ID);

  const timezone = moment.tz.guess(true);

  return {
    downloadingPdf: loading,
    pdfDownloadError: error,
    url: downloadWOPDF,
    getWOPdf: async (id, procedureIdsToInclude, sectionsToShow, uuid) =>
      download({
        variables: {
          input: { id, procedureIdsToInclude, sectionsToShow, uuid, timezone },
        },
        fetchPolicy: "network-only",
      }),
  };
};
